import { Controller } from '@hotwired/stimulus'
import {
  AllCommunityModule,
  ModuleRegistry,
  createGrid,
  InfiniteRowModelModule,
  TextFilterModule,
  themeAlpine
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  SetFilterModule,
  LicenseManager,
  ColumnMenuModule,
  RowNumbersModule,
  ContextMenuModule
} from 'ag-grid-enterprise'
import { format, parseISO, getDaysInMonth, getDay, setDate } from 'date-fns'
import AccessibleHeaderText from './grids/components/AccessibleHeaderText'
import GridConfigurationHelper from './grids/grid_configuration_helper'
import GridQueueHelper from './grids/queue_helper.js'
import GridFetchHelper from './grids/fetch_helper'
import { turboRequest } from '../../helpers/turbo_helper'
import { openModal} from '../../helpers/custom_events_helper'

ModuleRegistry.registerModules([
  AllCommunityModule,
  InfiniteRowModelModule,
  CellSelectionModule,
  ClipboardModule,
  TextFilterModule,
  SetFilterModule,
  ColumnMenuModule,
  RowNumbersModule,
  ContextMenuModule
])

const MAX_DAYS = 31
const DEFAULT_COLUMN_WIDTH = 128
const BACKGROUND_COLORS = {
  nonEditable: '#F1F1F1',
  temp: '#FFF9E3',
  default: '#FFFFFF',
  transparent: 'transparent',
  suspended: '#DFCFF0',
  unauthorized: '#EEEEEE'
}

const textFilterParams = {
  filter: 'agTextColumnFilter',
  filterParams: {
    filterOptions: ['contains'],
    maxNumConditions: 1
  },
  sortable: true
}

const caseInsensitiveComparator = (valueA, valueB) => {
  const a = (valueA || '').toString().toLowerCase()
  const b = (valueB || '').toString().toLowerCase()
  return a.localeCompare(b)
}

const myTheme = themeAlpine.withParams({
  browserColorScheme: 'light',
  cellHorizontalPaddingScale: 0.5,
  headerFontSize: 13,
  headerVerticalPaddingScale: 0.5,
  iconSize: 14,
  rowVerticalPaddingScale: 0.5
})

// constants for columns to maintain references, avoid typos
const COLUMNS = {
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  CAREGIVER: 'Caregiver',
  CARE_RECEIVER: 'Care receiver',
  CLIENT: 'Client',
  EMPLOYEE: 'Employee',
  SERVICE_DEF: 'Service Definition Name',
  FUNDING_SOURCE: 'Funding Source Name'
}

const ROW_HEADERS = [
  COLUMNS.FIRST_NAME,
  COLUMNS.LAST_NAME,
  COLUMNS.CAREGIVER,
  COLUMNS.CARE_RECEIVER,
  COLUMNS.CLIENT,
  COLUMNS.EMPLOYEE,
  COLUMNS.SERVICE_DEF,
  COLUMNS.FUNDING_SOURCE
]

export default class extends Controller {
  static targets = [
    'grid',
    'month',
    'toggleSubmission',
    'submit',
    'toggleWeekendColumns',
    'editGrid',
    'saveChanges',
    'loadingCount'
  ]

  static values = {
    valuesUrl: String,
    gridId: String,
    uploadId: String,
    licenseKey: String,
    usesScreenReader: Boolean,
    forCaregiver: Boolean,
    forEmployee: Boolean,
    saveConfigurationUrl: String,
    tableConfigData: Object,
    addZendeskSpacerRows: Boolean,
    clientPathTemplate: String,
    // This is used to determine how many customers are in a batch when we load the data
    customersFetchedPerBatch: {
      type: Number,
      default: 40
    },
    // This is used to determine how many items are in a batch when we process the queue creating temp service records
    bulkCreateBatchSize: {
      type: Number,
      default: 100
    },
    maxConcurrentCreateRequests: {
      type: Number,
      default: 5
    },
    maxConcurrentFetchRequests: {
      type: Number,
      default: 4
    }
  }

  connect() {
    LicenseManager.setLicenseKey(this.licenseKeyValue)

    this.liveRegion = document.getElementById('live-region-polite')

    this.uploadId = this.uploadIdValue
    this.setUploadId = this.setUploadId.bind(this)
    this.handleGlobalKeyDown = this.handleGlobalKeyDown.bind(this)
    this.pendingModalOperations = 0
    this.handleModalOperationStarted = this.handleModalOperationStarted.bind(this)
    this.handleModalOperationCompleted = this.handleModalOperationCompleted.bind(this)
    this.configHelper = new GridConfigurationHelper(
      null, // gridApi will be set later
      this.saveConfigurationUrlValue,
      this.liveRegion,
      this.usesScreenReaderValue
    )
    this.queueHelper = new GridQueueHelper({
      bulkCreateBatchSize: this.bulkCreateBatchSizeValue,
      uploadId: this.uploadId,
      updateSaveButtonState: this.updateSaveButtonState?.bind(this),
      maxConcurrentCreateRequests: this.maxConcurrentCreateRequestsValue,
      valuesUrl: this.valuesUrlValue,
      addZendeskSpacerRows: this.addZendeskSpacerRowsValue
    })
    this.gridFetchHelper = new GridFetchHelper({
      valuesUrl: `${this.valuesUrlValue}/${this.gridIdValue}`,
      customersFetchedPerBatch: this.customersFetchedPerBatchValue,
      uploadId: this.uploadId,
      maxConcurrentFetchRequests: this.maxConcurrentFetchRequestsValue
    })

    this.setupGrid()

    window.addEventListener('ag-grid:set-upload-id', this.setUploadId)
    window.addEventListener('ag-grid:update-cell-value', this.handleCellValueUpdate)
    window.addEventListener('ag-grid:modal-operation-started', this.handleModalOperationStarted)
    window.addEventListener('ag-grid:modal-operation-completed', this.handleModalOperationCompleted)
    document.addEventListener('keydown', this.handleGlobalKeyDown)
  }

  disconnect() {
    window.removeEventListener('ag-grid:set-upload-id', this.setUploadId)
    window.removeEventListener('ag-grid:update-cell-value', this.handleCellValueUpdate)
    window.removeEventListener('ag-grid:modal-operation-started', this.handleModalOperationStarted)
    window.removeEventListener('ag-grid:modal-operation-completed', this.handleModalOperationCompleted)
    document.removeEventListener('keydown', this.handleGlobalKeyDown)

    if (this.configHelper) {
      this.configHelper.cleanup()
    }

    this.gridTarget.innerHTML = ''
  }

  setupGrid() {
    const gridOptions = this.buildGridOptions()
    this.gridApi = createGrid(this.gridTarget, gridOptions)
    this.loadAllData(this.monthTarget.value)
  }

  async loadAllData(monthAsString) {
    try {
      this.gridApi.setGridOption('loading', true)
      this.gridApi.setGridOption('rowData', [])

      const result = await this.gridFetchHelper.fetchAllData(monthAsString)

      this.gridApi.setGridOption('rowData', result.rows)
      this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
      this.gridApi.setGridOption('loading', false)
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading data:', error)
        document.dispatchEvent(
          new CustomEvent('toast', {
            detail: {
              failure: true,
              message: 'Failed to load grid data. Please try refreshing the page.'
            }
          })
        )
        this.gridApi.setGridOption('loading', false)
      }
    }
  }

  onFilterChanged() {
    this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
  }

  buildGridOptions() {
    return {
      onCellEditingStarted: this.handleCellEditingStarted.bind(this),
      onCellValueChanged: this.handleCellValueChanged.bind(this),
      onCellEditingStopped: this.handleCellEditingStopped.bind(this),
      onCellClicked: this.handleCellClicked.bind(this),
      onFilterChanged: this.onFilterChanged.bind(this),
      onColumnMoved: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnVisible: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnPinned: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnResized: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onSortChanged: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      defaultColDef: this.defaultColumnDefinition(),
      columnDefs: this.buildColumnDefinitions(),
      enterNavigatesVerticallyAfterEdit: true,
      suppressRowHoverHighlight: true,
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 100,
      cellSelection: { handle: { mode: 'range' }, enableHeaderHighlight: true },
      rowNumbers: !this.usesScreenReaderValue,
      theme: myTheme,
      suppressContextMenu: false,
      getRowClass: params => {
        if (params.data && params.data.isSpacerRow) {
          return 'spacer-row'
        }
        return ''
      },
      allowContextMenuWithControlKey: false,
      enableBrowserTooltips: true,
      getContextMenuItems: params => {
        const result = []
        result.push('cut')
        result.push('copy')

        if (!this.uploadId) {
          result.push('separator')
          result.push('export')
        }

        return result
      },
      getRowStyle: params => {
        if (params.node.rowPinned === 'bottom') {
          return {
            fontWeight: '600',
            boxShadow: 'inset 0 4px 0 -2px #ccc'
          }
        }
        // Hide spacer rows for zendesk
        if (params.data && Object.keys(params.data).length === 0) {
          return {
            height: '0px',
            visibility: 'hidden'
          }
        }
        return null
      },
      onFirstDataRendered: () => {
        // ag-grid has no native capability to implement row headers, so we do some post-processing here
        // so that certain cell values will be read out during screen reader navigation
        const querySelectors = ROW_HEADERS.map(t => `div[role="gridcell"][col-id="${t}"]`)
        this.gridTarget.querySelectorAll(querySelectors.join(',')).forEach(node => {
          node.setAttribute('role', 'rowheader')
        })
      },
      onGridReady: params => {
        this.gridApi = params.api
        this.queueHelper.grid = params.api
        this.queueHelper.gridApi = params.api
        this.configHelper.setGridApi(this.gridApi)

        setTimeout(() => {
          this.configHelper.setIsInitialLoad(false)
        }, 2000)
      },
      onPasteStart: () => {
        this.pasteStart = new Date()
      }
    }
  }

  defaultColumnDefinition() {
    return {
      editable: true,
      sortable: false,
      width: 64
    }
  }

  buildColumnDefinitions() {
    const selectedDate = parseISO(this.monthTarget.value)

    let staticColumns = this.buildStaticColumns()
    const dynamicColumns = this.buildDynamicColumns(selectedDate)

    let allColumns = [...staticColumns, ...dynamicColumns]

    if (this.tableConfigDataValue && Object.keys(this.tableConfigDataValue).length > 0) {
      return this.configHelper.applyConfigurationToColumnDefs(allColumns, this.tableConfigDataValue)
    } else {
      return allColumns
    }
  }

  createClickableLink(params) {
    const { data, node, colDef, value } = params
    if (!data || node?.rowPinned === 'bottom') return value

    let key

    switch (colDef.field) {
      case COLUMNS.CAREGIVER:
        key = 'Caregiver Mon Ami ID'
        break
      case COLUMNS.CARE_RECEIVER:
        key = 'Care receiver Mon Ami ID'
        break
      case COLUMNS.CLIENT:
        key = 'Client Mon Ami ID'
        break
      case COLUMNS.FIRST_NAME:
      case COLUMNS.LAST_NAME:
        key = 'Client Mon Ami ID'
        break
      default:
        return value
    }

    const clientId = data[key]
    if (!clientId) return value

    const link = document.createElement('a')
    link.href = this.clientPathTemplateValue.replace('{clientId}', clientId)
    link.target = '_blank'
    link.innerText = value
    link.style.textDecoration = 'underline dotted #9E9E9E'
    link.style.color = 'unset'

    return link
  }

  nameColumnConfig(textFilterParams, sharedConfig, pinConfig, tooltip = false) {
    return {
      ...textFilterParams,
      ...sharedConfig,
      ...pinConfig,
      sortable: true,
      suppressColumnsToolPanel: true,
      lockVisible: true,
      cellRenderer: params => {
        return this.createClickableLink(params)
      },
      tooltipValueGetter: params => {
        if (params.node.rowPinned === 'bottom' && tooltip) {
          return params.value
        }
      }
    }
  }

  hiddenNameColumnConfig(textFilterParams, sharedConfig) {
    return {
      ...textFilterParams,
      ...sharedConfig,
      sortable: false,
      hide: true
    }
  }

  buildStaticColumns() {
    const sharedConfig = {
      width: DEFAULT_COLUMN_WIDTH,
      editable: false,
      lockPinned: this.usesScreenReaderValue,
      sortable: true,
      comparator: caseInsensitiveComparator
    }
    const pinConfig = {
      pinned: this.usesScreenReaderValue ? false : 'left'
    }

    const nameColumns = []

    if (this.forCaregiverValue) {
      nameColumns.push(
        {
          field: COLUMNS.CAREGIVER,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
        },
        {
          field: COLUMNS.CARE_RECEIVER,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
        }
      )
    } else if (this.forEmployeeValue) {
      nameColumns.push({
        field: COLUMNS.CLIENT,
        ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
      })
    } else {
      nameColumns.push(
        {
          field: COLUMNS.FIRST_NAME,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
        },
        {
          field: COLUMNS.LAST_NAME,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
        }
      )
    }

    if (this.forEmployeeValue) {
      nameColumns.push({
        field: COLUMNS.EMPLOYEE,
        ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
      })
    }

    return [
      ...nameColumns,
      ...(this.forCaregiverValue
        ? [
            {
              field: 'Caregiver Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            },
            {
              field: 'Care receiver Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]
        : [
            {
              field: 'Client Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]),
      ...(this.forEmployeeValue
        ? [
            {
              field: 'Employee Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]
        : []),
      {
        field: COLUMNS.SERVICE_DEF,
        ...sharedConfig,
        ...pinConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: COLUMNS.FUNDING_SOURCE,
        ...sharedConfig,
        ...pinConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: 'Program Name',
        ...sharedConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: 'Date of Birth',
        ...sharedConfig,
        sortable: true,
        hide: true,
        suppressColumnsToolPanel: this.forCaregiverValue || this.forEmployeeValue, // Temporarily hiding them for caregivers and employees; proper handling will be implemented in a separate PR.
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            const cellDate = new Date(cellValue)
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
          filterOptions: ['inRange', 'greaterThan', 'lessThan', 'equals', 'greaterThanOrEqual', 'lessThanOrEqual'],
          defaultOption: 'inRange',
          maxNumConditions: 1
        }
      },
      {
        field: 'County',
        hide: true,
        ...sharedConfig,
        ...textFilterParams,
        suppressColumnsToolPanel: this.forCaregiverValue || this.forEmployeeValue // Temporarily hiding them for caregivers and employees; proper handling will be implemented in a separate PR.
      },
      {
        field: 'Total Cost',
        resizable: true,
        width: 120,
        hide: true,
        suppressColumnsToolPanel: this.uploadId
      },
      {
        field: 'Month Total',
        headerName: 'Month Total',
        valueGetter: params => {
          if (!params.data) return 0
          return Object.keys(params.data)
            .filter(key => /^days_\d+$/.test(key))
            .reduce((sum, key) => {
              const val = parseFloat(params.data[key])
              return sum + (isNaN(val) ? 0 : val)
            }, 0)
        },
        valueFormatter: params => params.value?.toFixed(2),
        ...sharedConfig,
        ...pinConfig
      }
    ]
  }

  buildDynamicColumns(selectedDate) {
    return Array.from({ length: MAX_DAYS }, (_, i) => {
      const headerName = this.headerNameFromDate(setDate(selectedDate, i + 1))
      return {
        field: `days_${i + 1}`,
        headerName,
        headerComponentParams: {
          innerHeaderComponent: AccessibleHeaderText,
          innerHeaderComponentParams: { dateIndex: i + 1 }
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: params => ({
          min: 0,
          max: 1000,
          precision: params.data && params.data.accepts_partial_units ? 2 : 0
        }),
        valueParser: params => {
          const val = parseFloat(params.newValue)
          return isNaN(val) ? null : val
        },
        editable: params => {
          return (
            !!this.uploadId &&
            params.data?.[`days_${i + 1}_is_editable`] &&
            !params.data?.[`days_${i + 1}_is_unauthorized`] &&
            !params.data?.[`days_${i + 1}_is_multiple`] &&
            !params.data?.[`days_${i + 1}_has_automation`]
          )
        },
        cellRenderer: this.cellRendererForDayColumn(i + 1),
        cellStyle: this.cellStyleForDayColumn(i + 1),
        cellClassRules: {
          'suspended-cell': params => params.data?.[`days_${i + 1}_is_suspended`] && this.uploadId,
          'edited-suspended-cell': params =>
            params.data?.[`days_${i + 1}_is_suspended`] &&
            this.uploadId &&
            params.data[`days_${i + 1}_is_temp`] &&
            params.data[`days_${i + 1}`] != params.data[`days_${i + 1}_previous_value`]
        },
        hide: !this.shouldCellBeVisitable(selectedDate, i + 1, this.toggleWeekendColumnsTarget.checked),
        suppressHeaderMenuButton: true, // Stops the column header from seeing the 3 dots menu
        suppressMovable: true,
        suppressColumnsToolPanel: true, // Stops the column from being removed by choose columns menu
        lockPosition: 'right',
        width: 65
      }
    })
  }

  headerNameFromDate(date) {
    return format(date, 'M/d/yyyy')
  }

  cellRendererForDayColumn(day) {
    return params => {
      const isMultiple = params.data?.[`days_${day}_is_multiple`]
      const hasAutomation = params.data?.[`days_${day}_has_automation`]

      if (isMultiple) {
        return `${params.value} *`
      } else if (!isMultiple && hasAutomation) {
        return `${params.value} †`
      } else {
        return params.value
      }
    }
  }

  cellStyleForDayColumn(day) {
    return params => {
      if (params.node?.rowPinned === 'bottom') {
        return { backgroundColor: 'unset' }
      }
      if (!this.uploadId) {

        return { backgroundColor: BACKGROUND_COLORS.nonEditable, cursor: params.data[`days_${day}_has_automation`] || params.data[`days_${day}_is_multiple`] ? 'pointer' : 'default' }
      }
      if (!params.data) {
        return {}
      }
      if (!params.data[`days_${day}_is_editable`]) {
        return { backgroundColor: BACKGROUND_COLORS.nonEditable }
      }
      if (params.data[`days_${day}_is_unauthorized`]) {
        return { backgroundColor: BACKGROUND_COLORS.unauthorized }
      }
      if (params.data[`days_${day}_is_suspended`]) {
        return { backgroundColor: BACKGROUND_COLORS.suspended }
      }
      if (params.data[`days_${day}_is_multiple`] || params.data[`days_${day}_has_automation`]) {
        if (params.data[`days_${day}_is_temp`]) {
          return { backgroundColor: BACKGROUND_COLORS.temp, cursor: 'pointer' }
        } else {
          return { cursor: 'pointer' }
        }
      }
      if (
        params.data[`days_${day}_is_temp`] &&
        (() => {
          const currentValue = params.data[`days_${day}`] // null or number
          const previousValue = params.data[`days_${day}_previous_value`] // null or string

          // If both are null, no change
          if (currentValue === null && previousValue === null) {
            return false
          }

          // Convert previous value to number for comparison if it exists
          const previousNumber = previousValue === null ? null : Number(previousValue)

          // Return true if values are different
          return currentValue !== previousNumber
        })()
      ) {
        return { backgroundColor: BACKGROUND_COLORS.temp }
      }
      return params.value
        ? { backgroundColor: BACKGROUND_COLORS.default }
        : { backgroundColor: BACKGROUND_COLORS.transparent }
    }
  }

  calculateMonthTotal(row) {
    let total = 0
    for (let i = 1; i <= MAX_DAYS; i++) {
      const dayField = `days_${i}`
      const val = parseFloat(row[dayField]) || 0
      total += val
    }
    return total
  }

  handleCellValueChanged(event) {
    const {
      value,
      oldValue,
      data: rowData,
      colDef: { field }
    } = event

    // Skip if they don't change anything on an empty cell
    if (value === undefined && oldValue === null) {
      return
    }

    const day = field.split('_')[1]
    const selectedDate = parseISO(this.monthTarget.value)

    this.queueHelper.addItemToQueue({
      customer_id: rowData.customer_id,
      client_care_relationship_id: rowData.client_care_relationship_id,
      employee_id: rowData.employee_id,
      program_id: rowData.program_id,
      service_funding_source_id: rowData.funding_source_id,
      provider_id: rowData.provider_id,
      location_provider_id: rowData.location_provider_id,
      service_definition_id: rowData.service_definition_id,
      service_delivered_on: format(selectedDate, 'yyyy-MM-') + day,
      service_record_id: rowData[`days_${day}_service_record_id`],
      unit_count: value,
      old_unit_count: event.oldValue,
      node: event.node,
      field: field,
      day: day
    })
  }

  monthChanged(event) {
    const selectedDate = parseISO(event.target.value)
    this.hideMonthColumnsDependingOnDays(selectedDate, true, this.toggleWeekendColumnsTarget.checked)

    this.loadAllData(event.target.value)

    // Append the selected month to the URL
    this.replaceUrlWithMonth('report_month', event.target.value)
  }

  replaceUrlWithMonth(key, value) {
    const url = new URL(window.location.href)
    url.searchParams.set(key, value)
    window.history.replaceState(history.state, '', url)
  }

  hideMonthColumnsDependingOnDays(dateFnsDate, monthUpdated, hideWeekendColumns) {
    const columnsToShow = []
    const columnsToHide = []

    for (let i = 1; i <= MAX_DAYS; i++) {
      const columnName = `days_${i}`

      if (this.shouldCellBeVisitable(dateFnsDate, i, hideWeekendColumns)) {
        columnsToShow.push(columnName)
      } else {
        columnsToHide.push(columnName)
      }

      if (monthUpdated) {
        this.gridApi.getColumnDef(columnName).headerName = this.headerNameFromDate(setDate(dateFnsDate, i))
      }
    }

    if (columnsToShow.length) {
      this.gridApi.setColumnsVisible(columnsToShow, true)
    }
    if (columnsToHide.length) {
      this.gridApi.setColumnsVisible(columnsToHide, false)
    }

    this.gridApi.refreshCells({ force: true })
    this.gridApi.refreshHeader()
  }

  shouldCellBeVisitable(monthDate, i, hideWeekendColumns) {
    const daysInMonth = getDaysInMonth(monthDate)
    const date = setDate(monthDate, i)
    const dayOfWeek = getDay(date)
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    if (i <= daysInMonth && (!hideWeekendColumns || (hideWeekendColumns && !isWeekend))) {
      return true
    } else {
      return false
    }
  }

  startSubmission() {
    this.monthTarget.disabled = true
    this.editGridTargets.forEach(target => {
      target.classList.add('disabled')
    })
    this.toggleSubmissionTarget.classList.add('d-none')

    turboRequest(`${this.valuesUrlValue}/${this.gridIdValue}/uploads`, {
      method: 'POST'
    })

    this.hideTotalCostColumn()
  }

  hideTotalCostColumn() {
    var columnDefs = this.gridApi.getColumnDefs()
    const updatedColumnDefs = columnDefs.map(colDef => {
      if (colDef.field === 'Total Cost') {
        return { ...colDef, suppressColumnsToolPanel: true }
      }
      return colDef
    })
    this.gridApi.setGridOption('columnDefs', updatedColumnDefs)
    this.gridApi.setColumnsVisible(['Total Cost'], false)
  }

  // TODO: WHy does this get called twice when I click the checkbox?
  toggleWeekendColumns(event) {
    if (event.target.checked === undefined) {
      return
    }

    this.replaceUrlWithMonth('hide_weekend_columns', event.target.checked)
    this.hideMonthColumnsDependingOnDays(parseISO(this.monthTarget.value), false, event.target.checked)
  }

  setUploadId(event) {
    this.uploadId = event.detail.uploadId
    this.queueHelper.uploadId = this.uploadId

    const url = new URL(window.location.href)
    url.searchParams.set('submission_id', this.uploadId)
    window.history.replaceState(history.state, '', url)

    this.gridApi.refreshCells({ force: true })
  }

  handleCellValueUpdate = (event) => {
    const detail = event.detail

    const node = this.gridApi.getRowNode(detail.row_node_id)
    if (!node?.data) return

    const field = detail.column_field
    node.data[field] = detail.new_total
    node.data[`${field}_is_temp`] = true
    node.data[`${field}_is_multiple`] = true
    this.gridApi.refreshCells({ rowNodes: [node], force: true })
    this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
  }

  handleCellEditingStarted() {
    this.updateSaveButtonState()
  }

  handleCellEditingStopped() {
    this.updateSaveButtonState()
  }

  handleCellClicked(event) {
    const { colDef, data, node } = event
    if (!colDef || !data) return

    const field = colDef.field
    if (!field || !field.startsWith('days_')) return

    const day = field.split('_')[1]

    if ((data[`days_${day}_is_multiple`] || data[`days_${day}_has_automation`])) {
      this.showMultipleRecordsModal(data, day, node.id, field)
    }
  }

  showMultipleRecordsModal(data, day, rowNodeId, columnField) {
    const serviceDeliveredOn = format(parseISO(this.monthTarget.value), 'yyyy-MM-') + String(day).padStart(2, '0')

    const params = this.buildMultipleRecordsParams({
      data,
      serviceDeliveredOn,
      rowNodeId,
      columnField,
      uploadId: this.uploadId
    })

    const idPrefix = 'grid-multiple-records'
    const modalContainer = document.getElementById(`${idPrefix}-container`)
    const dialog = document.querySelector(`dialog#${idPrefix}_dialog`)
    const frame = dialog.querySelector(`turbo-frame#${idPrefix}_frame`)
    const url = `${this.valuesUrlValue}/${this.gridIdValue}/multiple_service_records_modal?${params.toString()}`
    frame.src = url

    openModal(modalContainer)
  }

  buildMultipleRecordsParams({ data, serviceDeliveredOn, rowNodeId, columnField, uploadId }) {
    const params = new URLSearchParams()

    params.append('service_delivered_on', serviceDeliveredOn)
    params.append('row_node_id', rowNodeId)
    params.append('column_field', columnField)

    const dataFields = [
      'program_id',
      'provider_id',
      'service_definition_id',
      'customer_id',
      'client_care_relationship_id',
      'employee_id',
      'location_provider_id'
    ]

    dataFields.forEach(field => {
      if (data[field] != null) {
        params.append(field, data[field])
      }
    })

    if (data.funding_source_id != null) {
      params.append('service_funding_source_id', data.funding_source_id)
    }

    if (uploadId) {
      params.append('upload_id', uploadId)
    }

    return params
  }

  handleModalOperationStarted() {
    this.pendingModalOperations++
    this.updateSaveButtonState()
  }

  handleModalOperationCompleted() {
    this.pendingModalOperations--
    this.updateSaveButtonState()
  }

  updateSaveButtonState() {
    if (!this.hasSaveChangesTarget) return

    const isEditing = this.gridApi.getEditingCells().length > 0

    if (isEditing || this.queueHelper.hasQueueItems() || this.pendingModalOperations > 0) {
      this.saveChangesTarget.setAttribute('disabled', true)
      this.saveChangesTarget.classList.add('disabled')
      this.dispatchBsTooltipEvent(true)
    } else {
      this.saveChangesTarget.removeAttribute('disabled')
      this.saveChangesTarget.classList.remove('disabled')
      this.dispatchBsTooltipEvent(false)
    }
  }

  dispatchBsTooltipEvent(enable) {
    const customEvent = new CustomEvent('enabled.bs.tooltip', {
      detail: {
        enable
      }
    })

    this.saveChangesTarget.parentNode.dispatchEvent(customEvent)
  }

  handleGlobalKeyDown(event) {
    // Check if we're in edit mode by looking for editing cells
    const editingCells = this.gridApi.getEditingCells()
    if (editingCells.length === 0) return

    const key = event.key
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      // Prevent default behavior
      event.preventDefault()
      event.stopPropagation()

      // Get current focused cell
      const focusedCell = this.gridApi.getFocusedCell()
      if (!focusedCell) return

      let nextRow = focusedCell.rowIndex
      let nextCol = focusedCell.column.colId

      // Calculate next cell position based on arrow key
      switch (key) {
        case 'ArrowUp':
          nextRow = Math.max(0, focusedCell.rowIndex - 1)
          break
        case 'ArrowDown':
          nextRow = focusedCell.rowIndex + 1
          break
        case 'ArrowLeft':
          // Find previous editable column
          const allColumns = this.gridApi.getAllDisplayedColumns()
          const currentColIndex = allColumns.findIndex(col => col.colId === focusedCell.column.colId)
          if (currentColIndex > 0) {
            nextCol = allColumns[currentColIndex - 1].colId
          }
          break
        case 'ArrowRight':
          // Find next editable column
          const allCols = this.gridApi.getAllDisplayedColumns()
          const currentColIdx = allCols.findIndex(col => col.colId === focusedCell.column.colId)
          if (currentColIdx < allCols.length - 1) {
            nextCol = allCols[currentColIdx + 1].colId
          }
          break
      }

      // Stop current editing and navigate
      this.gridApi.stopEditing()

      // Navigate to next cell
      this.gridApi.setFocusedCell(nextRow, nextCol)
    }
  }

  calculatePinnedBottomRow() {
    const filteredData = []
    const uniqueClients = new Set()

    this.gridApi.forEachNodeAfterFilter(node => {
      if (node.data && Object.keys(node.data).length > 0) {
        filteredData.push(node.data)

        let key = null

        if (this.forCaregiverValue) {
          key = node.data['client_care_relationship_id']
        } else {
          key = node.data['Client Mon Ami ID']
        }

        if (this.forEmployeeValue) {
          key = `${key}_${node.data['employee_id']}`
        }

        uniqueClients.add(key)
      }
    })

    if (filteredData.length === 0) {
      return [{}]
    }

    const totals = {}

    for (let i = 1; i <= MAX_DAYS; i++) {
      const dayField = `days_${i}`
      totals[dayField] = filteredData
        .reduce((sum, row) => {
          const val = parseFloat(row[dayField]) || 0
          return sum + val
        }, 0)
        .toFixed(2)
    }

    const allColumns = this.gridApi.getAllDisplayedColumns()
    const clientOrCaregiverField = this.forCaregiverValue
      ? COLUMNS.CAREGIVER
      : this.forEmployeeValue
      ? COLUMNS.CLIENT
      : COLUMNS.FIRST_NAME
    const targetColumn = allColumns.find(col => col.colId === clientOrCaregiverField)

    let text = `Total: ${uniqueClients.size} unduplicated clients`
    if (this.forCaregiverValue) {
      text = `Total: ${uniqueClients.size} unduplicated care relationships`
    }

    if (this.forEmployeeValue) {
      text = text.replace('unduplicated', 'unduplicated employee -')
    }
    totals[targetColumn.colId] = text

    let spacerRows = []
    if (this.addZendeskSpacerRowsValue) {
      // Add 2 empty spacer rows before the totals row for zendesk
      spacerRows = Array.from({ length: 2 }, () => ({ isSpacerRow: true }))
    }

    return [...spacerRows, totals]
  }
}
